import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import { useQuery } from '@apollo/client';

import { breakpoints } from '@notino/react-styleguide';
import {
  GetRecommendedProductsQueryVariables,
  GetRecommendedProductsQuery,
} from '@notino/shared/definitions/types';

import { useProductSectionsStateInfo } from '@containers/ProductDetailContainer/useProductSectionsStateInfo';
import { IProduct } from '@containers/ProductListing/model';
import { styled } from '@linaria/react';
import { RECOMMENDATION_PLACEMENT } from '@utils/tracking/recommendationPlacement';
import {
  useRecommendationTracking,
  useTrackRecommendationFailure,
} from '@utils/tracking/useRecommendationTracking';

import messages from '../../messages';
import { useProductDetailContext } from '../../ProductDetailContext';
import getRecommendedProductsQuery from '../../queries/databreakersRecommendedProducts.graphql';
import { ProductsSection } from '../ProductsSection';

import { LIST_LABEL } from './constants';

const MAX_RELATED_PRODUCTS = 12;

export interface IRelatedProductsProps {
  items: Array<IProduct['id']>;
  currentProductCode: string;
  shouldRenderRecommendedProducts?: boolean;
}

const Wrap = styled.div`
  margin-bottom: 2rem;
  @media (min-width: ${breakpoints.lg}) {
    margin-bottom: 4rem;
  }
`;

export const RelatedProducts: React.FC<IRelatedProductsProps> = React.memo(
  ({ items, currentProductCode, shouldRenderRecommendedProducts = false }) => {
    const [ref, inView] = useInView({ triggerOnce: true });
    const { setDisplayedRelatedProductIds } = useProductSectionsStateInfo(
      (state) => state.actions
    );
    const {
      product: { brand },
    } = useProductDetailContext();

    const { data, loading, error } = useQuery<
      GetRecommendedProductsQuery,
      GetRecommendedProductsQueryVariables
    >(getRecommendedProductsQuery, {
      variables: {
        productCode: currentProductCode,
        brandId: brand?.id ? Number(brand.id) : null,
      },
      skip: !shouldRenderRecommendedProducts || !inView,
      ssr: false,
    });

    const recommendationAttributes =
      data?.recommendedProducts?.recommendationAttributes ?? undefined;

    const { trackFailed } = useRecommendationTracking(
      RECOMMENDATION_PLACEMENT.pdpRelatedProducts,
      recommendationAttributes
    );

    useTrackRecommendationFailure(
      trackFailed,
      data?.recommendedProducts,
      error
    );

    const relatedProductsIds = React.useMemo(
      () =>
        data?.recommendedProducts?.recommendedProducts?.length
          ? data.recommendedProducts.recommendedProducts
              .filter((p): p is NonNullable<typeof p> => p != null)
              .map((p) => p.productId)
              .filter((id): id is string => id != null)
          : items,
      [data, items]
    );

    React.useEffect(() => {
      setDisplayedRelatedProductIds(relatedProductsIds.map(Number) ?? []);
    }, [relatedProductsIds, setDisplayedRelatedProductIds]);

    return (
      <div ref={ref} data-testid="related-products-wrapper">
        {inView && !window.recoProducts?.tracking && (
          <Wrap>
            <ProductsSection
              productIds={
                relatedProductsIds?.slice(0, MAX_RELATED_PRODUCTS) ?? []
              }
              productIdsLoading={loading}
              titleMessage={messages.related}
              titleId="pdRelatedProducts"
              containerId="pd-related-products-header"
              listLabel={LIST_LABEL}
              withDivider={false}
              trackProductClick={true}
              trackProductImpression={true}
              recommendationPlacement={
                RECOMMENDATION_PLACEMENT.pdpRelatedProducts
              }
              recommendationAttributes={recommendationAttributes}
              attributionToken={
                data?.recommendedProducts?.recommendationAttributes
                  ?.attributionToken ?? undefined
              }
            />
          </Wrap>
        )}
      </div>
    );
  }
);
